/* eslint-disable no-nested-ternary */
import * as vp from 'vopay';
import * as Yup from 'yup';
import { FunctionComponent, JSX } from 'react';
import { Formik } from 'formik';
import { buildSubAccountRows } from './buildSubAccountRows.helper';
import { SubAccountCreationHandler, SubAccountCreationRequest } from './types';
import { useTranslation } from 'react-i18next';

const { Box, Button, Table, TextField, Select, Icon, Tooltip } = vp;

type SubAccountCreationProps = {
  handlers: SubAccountCreationHandler;
  defaultValues: SubAccountCreationRequest;
};

type FC = FunctionComponent<SubAccountCreationProps>;
const SubAccountCreationForm: FC = (props: SubAccountCreationProps) => {
  const { handlers, defaultValues } = props;
  const SubAccountRows = buildSubAccountRows();
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.Create' });

  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={handlers.onSubAccountCreation}
      validationSchema={createdValidationSchema()}
      validateOnChange
      validateOnBlur
      enableReinitialize
    >
      {({ values, touched, errors, handleChange, handleBlur, handleSubmit }) => {
        const subAccountCreationTable: vp.TableProps = {
          name: 'sub-account-creation-fields',
          variant: 'pair',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: SubAccountRows.filter(({ visible }) => visible).map(
            ({ label, id, require, preset, type, placeholder, maxLength, information, getSelectOptions }) => ({
              Field: (
                <Box jc="fs" flx="col">
                  <Box ai="center">
                    {label} {getRequiredIcon(require)}
                    {information && (
                      <Tooltip
                        type="hover"
                        information={information}
                        position="right"
                        hasArrow={false}
                        icon="question"
                      />
                    )}
                  </Box>
                  {id === 'EmailAddress' && <Box maxwidth="400px" />}
                  {id === 'OriginatorName' && <Box maxwidth="340px" />}
                  {id === 'OriginatorShortName' && <Box maxwidth="340px" />}
                </Box>
              ),
              Input:
                type === 'input' ? (
                  <TextField
                    preset={preset}
                    key={id}
                    name={id}
                    variant="standard"
                    placeholder={placeholder}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    maxLength={maxLength}
                    value={String(values[id])}
                    error={errors[id] && touched[id] ? errors[id] : null}
                  />
                ) : (
                  <Select
                    variant="standard"
                    options={getSelectOptions()}
                    key={id}
                    name={id}
                    value={String(values[id])}
                    placeholder={placeholder}
                    noOrdering
                    onBlur={handleBlur}
                    onChange={(e) => {
                      handleChange(e);
                    }}
                    error={errors[id] && touched[id] ? errors[id] : null}
                  />
                ),
            }),
          ),
        };

        return (
          <form onSubmit={handleSubmit}>
            <Table {...subAccountCreationTable} />
            <Box mt="md" jc="fe">
              <Button id="Sub-Account-Creation-Review" type="submit" label={t('Review.submitButton')} />
            </Box>
          </form>
        );
      }}
    </Formik>
  );

  function createdValidationSchema() {
    return Yup.object().shape({
      LegalBusinessName: Yup.string().label(t('Form.LegalBusinessName.label')).required(),
      OriginatorName: Yup.string()
        .matches(/^.*\S+.*/, t('Form.Validation.cannotContainOnlySpaces'))
        .required(),
      OriginatorShortName: Yup.string()
        .matches(/^.*\S+.*/, t('Form.Validation.cannotContainOnlySpaces'))
        .required(),
      EmailAddress: Yup.string().required().email(t('Form.Validation.invalidEmail')),
    });
  }
  function getRequiredIcon(isRequired: boolean): JSX.Element {
    return isRequired ? <Icon icon="asterisk" size="xsm" color="error" /> : <></>;
  }
};

export default SubAccountCreationForm;
